import type { ReactNode } from "react";
import {
  IconNameEnum,
  useModuleBEM,
  useUiStyles,
} from "@b2bportal/core-themes";
import {
  CoreUiComponents,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import type { TooltipProps as RadixTooltipProps } from "@radix-ui/react-tooltip";
import { Icon, Popover, PopoverContent, PopoverTrigger } from "@tenant/ui";
import defaultStyles from "./Tooltip.module.scss";
import { TenantIconNameEnum } from "@/types/tenantIcons";

/**
 * How the tooltip is drawn.
 * - `popover`: the portal's panel, below the trigger (default).
 * - `flyout`: the RBC design-system "Tooltip Flyout": a bordered panel with a
 *   nubbin, meant for one-line terms inside dialogs.
 */
export type TooltipVariant = "popover" | "flyout";

export type TooltipType = RadixTooltipProps & {
  /** Visible text inside the trigger button, before the icon. Omit for an icon-only trigger. */
  children?: ReactNode;
  content: ReactNode;
  customIcon?: ReactNode;
  variant?: TooltipVariant;
  /** Which side of the trigger the panel opens on. Radix default: bottom. */
  side?: PopoverPrimitive.PopoverContentProps["side"];
  /** Accessible name of the trigger button, for icon-only triggers. */
  triggerLabel?: string;
  /** Accessible name of the open panel. */
  contentLabel?: string;
} & PlatformComponentProps;

const COMPONENT_KEY = CoreUiComponents.Tooltip;

// Nubbin size from the design system flyout; the offset keeps its tip clear of the trigger.
const FLYOUT_ARROW_WIDTH = 18;
const FLYOUT_ARROW_HEIGHT = 9;
const FLYOUT_SIDE_OFFSET = 7;

export const Tooltip = ({
  children,
  content,
  customIcon,
  variant = "popover",
  side,
  triggerLabel,
  contentLabel,
}: TooltipType) => {
  const { t } = useI18nContext();
  const styles = useUiStyles(COMPONENT_KEY, defaultStyles);
  const [, cn] = useModuleBEM(styles, COMPONENT_KEY);
  const isFlyout = variant === "flyout";
  const flyoutContentProps = isFlyout
    ? { align: "center" as const, sideOffset: FLYOUT_SIDE_OFFSET }
    : {};

  return (
    <Popover>
      <PopoverTrigger aria-label={triggerLabel}>
        <div className={cn("label")}>
          {children}
          {customIcon ? (
            customIcon
          ) : (
            <Icon
              className={cn("icon")}
              iconName={IconNameEnum.ui__infoPopover}
            />
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent
        className={cn("content", { flyout: isFlyout })}
        // The flyout draws its own close button: the design-system glyph at 24px.
        showCloseButton={!isFlyout}
        side={side}
        aria-label={contentLabel}
        {...flyoutContentProps}
      >
        {content}
        {isFlyout && (
          <>
            <PopoverPrimitive.Close
              className={cn("close")}
              aria-label={t("close.button")}
            >
              <Icon iconName={TenantIconNameEnum.timesAlt} aria-hidden />
            </PopoverPrimitive.Close>
            <PopoverPrimitive.Arrow
              className={cn("arrow")}
              width={FLYOUT_ARROW_WIDTH}
              height={FLYOUT_ARROW_HEIGHT}
            />
          </>
        )}
      </PopoverContent>
    </Popover>
  );
};
